import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight, LayoutGrid, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SOLUTION_GROUPS, SolutionGroupContent, groupPath, itemPath, useSolutionTitles } from "./content";

// medidas em volta do centro (500, 500); o viewBox corta logo depois do círculo tracejado, sem margem sobrando,
// para o desenho ocupar toda a área do componente
const C = 500;
const CORE = 140; // círculo do centro
const TOPIC = [150, 362]; // anel dos tópicos (raio interno e externo)
const ITEM = [370, 404]; // anel fino das soluções (só as fatias, sem nome)
const GUIDE = 413; // círculo tracejado em volta
const VIEW = 418; // metade do viewBox
const TOPIC_TEXT = 310; // nome do tópico, na parte de fora do anel
const TOPIC_ICON = 224; // ícone do tópico, na parte de dentro
const ICON = 34;

const TOTAL = SOLUTION_GROUPS.reduce((sum, g) => sum + g.items.length, 0);
// distribuição simétrica: todos os tópicos com a mesma fatia, e as soluções dividem igualmente a fatia do seu tópico
const SPAN = 360 / SOLUTION_GROUPS.length;
const START = -90 - SPAN / 2; // o primeiro tópico fica centrado no topo

const point = (r: number, deg: number) => {
  const rad = (deg * Math.PI) / 180;
  return [C + r * Math.cos(rad), C + r * Math.sin(rad)];
};
const polar = (r: number, deg: number) => point(r, deg).map((n) => n.toFixed(2)).join(" ");

// fatia de anel entre os raios r0 e r1, do ângulo a0 ao a1 (graus, sentido horário)
const slice = (r0: number, r1: number, a0: number, a1: number) => {
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M ${polar(r1, a0)} A ${r1} ${r1} 0 ${large} 1 ${polar(r1, a1)} L ${polar(r0, a1)} A ${r0} ${r0} 0 ${large} 0 ${polar(r0, a0)} Z`;
};

// arco por onde corre o nome do tópico; na metade de baixo vai ao contrário para o texto não ficar de cabeça para baixo
const textArc = (r: number, a0: number, a1: number) => {
  const mid = ((((a0 + a1) / 2) % 360) + 360) % 360;
  const bottom = mid > 0 && mid < 180;
  return bottom ? `M ${polar(r, a1)} A ${r} ${r} 0 0 0 ${polar(r, a0)}` : `M ${polar(r, a0)} A ${r} ${r} 0 0 1 ${polar(r, a1)}`;
};

// posição de cada tópico e de cada solução no círculo
const LAYOUT = (() => {
  let index = 0;
  return SOLUTION_GROUPS.map((group, g) => {
    const a0 = START + g * SPAN;
    const step = SPAN / group.items.length;
    const items = group.items.map((item, i) => ({ item, a0: a0 + i * step, a1: a0 + (i + 1) * step, index: index++ }));
    return { group, a0, a1: a0 + SPAN, items };
  });
})();

type Open = { group: SolutionGroupContent; itemKey?: string };

// Informativo do tópico: abre por cima de tudo (portal no body), vindo para perto de quem olha, com as soluções do
// tópico em cards para clicar. Fecha ao clicar fora, Esc ou no X.
function TopicPopup({ open, onClose }: { open: Open; onClose: () => void }) {
  const { t } = useTranslation();
  const { groupTitle, itemTitle } = useSolutionTitles();
  const { group, itemKey } = open;
  const Icon = group.icon;
  const title = groupTitle(group);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus({ preventScroll: true });
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div className="cz-scope cz-wheel-pop" role="dialog" aria-modal="true" aria-labelledby="cz-wheel-pop-title">
      <div className="cz-wheel-pop__backdrop" onClick={onClose} />
      <div className="cz-wheel-pop__panel">
        <button ref={closeRef} type="button" className="cz-wheel-pop__close" onClick={onClose} aria-label={t("header.menu_close", "Fechar")}>
          <X size={20} />
        </button>

        <header className="cz-wheel-pop__head">
          <span className="cz-wheel-pop__icon" aria-hidden="true">
            <Icon size={30} strokeWidth={1.6} />
          </span>
          <div className="cz-wheel-pop__heading">
            <span className="cz-eyebrow">
              {t("solutions_catalog.items_count", { count: group.items.length, defaultValue: "{{count}} soluções" })}
            </span>
            <h2 className="cz-wheel-pop__title" id="cz-wheel-pop-title">{title}</h2>
            <p className="cz-wheel-pop__tagline">{group.tagline}</p>
          </div>
        </header>

        <ul className="cz-wheel-pop__items">
          {group.items.map((item, i) => {
            const ItemIcon = item.icon;
            return (
              <li key={item.key} style={{ "--i": i } as React.CSSProperties}>
                <Link to={itemPath(group, item)} className={`cz-wheel-pop__item${item.key === itemKey ? " is-active" : ""}`}>
                  <span className="cz-wheel-pop__item-icon" aria-hidden="true">
                    <ItemIcon size={20} strokeWidth={1.7} />
                  </span>
                  <span className="cz-wheel-pop__item-text">
                    <strong>{itemTitle(group, item)}</strong>
                    <span>{item.tagline}</span>
                  </span>
                  <ArrowUpRight size={17} className="cz-wheel-pop__item-go" aria-hidden="true" />
                </Link>
              </li>
            );
          })}
        </ul>

        <Link to={groupPath(group)} className="cz-outline-btn cz-outline-btn--pink cz-wheel-pop__all">
          {t("solutions_catalog.see_topic", { name: title, defaultValue: "Ver tudo sobre {{name}}" })}
          <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
    </div>,
    document.body
  );
}

// Mapa das soluções em círculo: os tópicos do menu no anel grande (ícone e nome dentro da fatia) e, em volta, um
// anel fino com uma fatia por solução. Passar o mouse destaca o tópico; clicar (ou Enter pelo teclado) abre o
// informativo dele. /solucoes#<tópico> abre o informativo daquele tópico ao chegar.
export default function SolutionsWheel() {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const { groupTitle } = useSolutionTitles();
  const [hover, setHover] = useState<string | null>(null);
  const [open, setOpen] = useState<Open | null>(null);
  const close = useCallback(() => setOpen(null), []);

  useEffect(() => {
    const group = SOLUTION_GROUPS.find((g) => `#${g.slug}` === hash);
    if (group && window.matchMedia("(min-width: 641px)").matches) setOpen({ group });
  }, [hash]);

  const handlers = (next: Open) => ({
    onMouseEnter: () => setHover(next.group.key),
    onMouseLeave: () => setHover(null),
    onClick: () => setOpen(next),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      setOpen(next);
    },
    onFocus: () => setHover(next.group.key),
    onBlur: () => setHover(null),
  });

  const highlighted = open?.group.key ?? hover;

  return (
    <div className={`cz-wheel${highlighted ? " has-active" : ""}`}>
      <svg className="cz-wheel__svg" viewBox={`${C - VIEW} ${C - VIEW} ${VIEW * 2} ${VIEW * 2}`} role="group" aria-label={t("solutions_catalog.map_label", "Mapa das soluções")}>
        <circle className="cz-wheel__guide" cx={C} cy={C} r={GUIDE} />

        {LAYOUT.map(({ group, a0, a1, items }, g) => {
          const title = groupTitle(group);
          const arcId = `cz-wheel-arc-${group.key}`;
          const Icon = group.icon;
          const [ix, iy] = point(TOPIC_ICON, (a0 + a1) / 2);
          return (
            <g
              key={group.key}
              className={`cz-wheel__group${highlighted === group.key ? " is-active" : ""}`}
              style={{ "--g": g } as React.CSSProperties}
            >
              <g
                className="cz-wheel__topic"
                role="button"
                tabIndex={0}
                aria-haspopup="dialog"
                aria-label={title}
                {...handlers({ group })}
              >
                <path className={`cz-wheel__topic-slice${g % 2 ? " is-alt" : ""}`} d={slice(TOPIC[0], TOPIC[1], a0, a1)} />
                <Icon
                  className="cz-wheel__topic-icon"
                  x={ix - ICON / 2}
                  y={iy - ICON / 2}
                  width={ICON}
                  height={ICON}
                  strokeWidth={1.6}
                  aria-hidden="true"
                />
                <path id={arcId} d={textArc(TOPIC_TEXT, a0, a1)} fill="none" />
                <text className="cz-wheel__topic-label" dy="7">
                  <textPath href={`#${arcId}`} startOffset="50%" textAnchor="middle">
                    {title}
                  </textPath>
                </text>
              </g>

              {items.map(({ item, a0: b0, a1: b1, index }) => (
                <path
                  key={item.key}
                  className="cz-wheel__item-slice"
                  style={{ "--i": index } as React.CSSProperties}
                  d={slice(ITEM[0], ITEM[1], b0, b1)}
                  {...handlers({ group, itemKey: item.key })}
                />
              ))}
            </g>
          );
        })}

        <circle className="cz-wheel__core" cx={C} cy={C} r={CORE} />
      </svg>

      <div className="cz-wheel__center" aria-hidden="true">
        <span className="cz-wheel__center-icon">
          <LayoutGrid size={34} strokeWidth={1.6} />
        </span>
        <strong className="cz-wheel__center-title">{t("solutions_catalog.center_title", "Soluções Calriz")}</strong>
        <span className="cz-wheel__center-meta">
          {t("solutions_catalog.center_meta", {
            groups: SOLUTION_GROUPS.length,
            items: TOTAL,
            defaultValue: "{{items}} soluções em {{groups}} áreas",
          })}
        </span>
      </div>

      {open && <TopicPopup open={open} onClose={close} />}
    </div>
  );
}
